import { Baseline, FormulaEngineInstance, IncExcKey, Measure, VisionKey, VisionStep, WidgetConfigurationItemObj, WidgetConfigurationObj } from '@wizeoo/akm-message-lib';
import { BaselineEngine, FIELD_KIND, FeiKeyDef, KEY_KIND, MeasureVO, VisionVO, WidgetLine, WizConstants } from '@wizeoo/akm-compute-lib';
import { Model } from '../../model/model';
import { FeiEngine } from './fei.engine';
import { cloneDeep } from 'lodash-es';
import { FeiVO } from '../../vo/workingdata/fei.vo';
import { FreeDataVO } from '../../vo/core/freedata.vo';
import { getDefaultWidgetConfigurationObj } from '../widgets/widget.engine';
import { AggregateEngine } from '../computer/aggregate-engine';
import { FreedataValueVO } from '../../vo/core/freedatavalue.vo';
import { IWidgetFilter, IWidgetFilterItem } from '../../manager/widgets/i-widget';
import { IncExcKeyVO } from '../../vo/core/incexckey.vo';
import { FreedataArgsVO } from '../../vo/core/freedataargs.vo';
import { ThresholdField, ThresholdLine } from '../../components/widgets/pivot/renderers/cells/pivot-cell-threshold-edit.pop';

/**
 * les données nécessaires à une PopUp ListField
 * alimentées depuis mesure.settings et widgetline.cell
 */
export interface ListFieldGridDatas {
  fei: FeiVO; // fei pour collecte des mesures
  measure: Measure; // la mesure source
  valueMap: Map<string, string>; // les valeurs par codeFDV
  renderFEI: FeiVO; // les FEIs fake générés pour le rendu
  context: WidgetConfigurationObj; // le contexte (les colonnes)
  filter?:string; // filtre par défaut
  subTotalControl?: Measure; // cohérence sur les sous-totaux
  // grandTotalControl?: MeasureVO; // cohérence sur le total général
}

/**
 * la configuration JSON du ListField (measure.settings)
 */
export interface LFSettings {
  keys: LFKey[]; // la configuration des FDs
  measure: string; // le code de la mesure à utiliser
  filter?: string; // un filtre par défaut
  controlMeasure?: string; // le meas#mode d'une mesure pour le contrôle de cohérence du total intermédiaire
  controlKind?: string; // <=> !=
  // grandTotalControlMeasure?: string; // le meas#mode d'une mesure pour le contrôle de cohérence du total

}

/**
 * une configuration FD 
 */
export interface LFKey {
  FD: string; // le couple codeFD#level
  order: number; // ordre d'apparition
  fields: LFField[]; // la configuration de champs
}

/**
 * un champ de configuration de listField
 */
export interface LFField {
  code: string; // le champ sur le FDV (basic ou args)
  order?: number; // l'ordre de la colonne de rendu
  sortField?: string; // le champ qui sert pour le tri
  width?: number; // la taille de colonne
  label?: string; // l'éventuel label pour l'entête, sinon on prend le code du FD
  render?: boolean; // colonne utilisée pour le rendu Grid.
}

/**
 * configuration du rendu pour affichage
 */
export interface LFStructuralRender {
  order: number; // ordre d'apparition
  freedata: FreeDataVO; // le freedata porteur de la dimension
  field: string; // le champ à afficher
  subs?: LFStructuralSubRender[];
  // parenthesis?: boolean; // utiliser des parenthèses pour le rendu (plusieurs champs sur le FD)
  // nextLevel?: boolean; // on a changé de level / FD
}

/**
 * configuration du rendu pour affichage
 */
export interface LFStructuralSubRender {
  field: string; // le champ à afficher
  parenthesis?: boolean; // utiliser des parenthèses pour le rendu (plusieurs champs sur le FD)
  nextLevel?: boolean; // on a changé de level / FD
}

/**
 * élément de rendu d'affichage
 */
export interface LFRender {
  code: string; // le code du FD principal
  label: string; // son libellé, selon le champ utilisé
  value: string; // le freedata porteur de la dimension
  subMap: Map<string, LFRender> ; // map de détail si 2 niveaux
}

/**
 * gestion spécifique des mesures de type liste
 */
export class ListFieldEngine {

  protected _model: Model = null;
  public get model(): Model {
    return this._model as Model;
  }
  constructor(model: Model) {
    this._model = model;
  }


  // ######################################
  // gestion de la popUp de rendu
  // ######################################

  /**
   * construction des paramètres du GRID
   * @param fei
   * @param fkd 
   * @param lines
   */
  public buildGridParameters(fei: FeiVO, fkd: FeiKeyDef, lines: WidgetLine[]): ListFieldGridDatas {
    
    // la vraie mesure (LIST)
    let sourceMeasure: Measure = fkd.measure; 
    let targetMeasure: MeasureVO;

    // la configuration pour le rendu en mode liste
    let settings: LFSettings = sourceMeasure.settings as LFSettings;

    let valueMap: Map<string, string> = new Map(); // les valeurs par codeFDV
    let freedata: FreeDataVO; // FD porteur
    let renderFEI: FeiVO; // fake FEI pour le rendu
    let renderFakeFKD: FeiKeyDef; // fausse KeyDef
    
    // prise de la mesure de contrôle sur les cumuls
    let controlMeasureSource: MeasureVO;
    let controlMeasureValue: string = null;
    let controlMeasure: Measure;

    //##### génération des fausses Baselines pour la restitution par niveaux
    const vkeys: {[key: number]: VisionKey} = {};
    const steps: {[key: number]: VisionStep} = {};
 
    // création des clés pour la fausse vision
    let fakeVis: VisionVO = cloneDeep(this.model.currentVision);
    let visionKey: VisionKey;
    let step: VisionStep;

    // et au passage, création du contexte pour les colonnes de rendu
    let context: WidgetConfigurationObj = getDefaultWidgetConfigurationObj();
    context.r = [];
    context.d = [];
    let colIdx: number = 1; // indice de la colonne

    if(settings.keys.length > 1) {
      context.pmd = true; // mode consolidation pas si un seul FD
    } else {
      context.pmd = false; 
    }

    try {

      // prise de la mesure cible
      targetMeasure = this.model.measureEngine.getMeasure(fei.visionCode, fei.visionStepLevel, settings.measure);

      // et du contrôle sur total
      if(settings.controlMeasure && lines?.length > 0) {
        controlMeasureSource = this.model.measureEngine.getMeasure(fei.visionCode, fei.visionStepLevel, settings.controlMeasure);
        controlMeasureValue = BaselineEngine.getValue(fei, lines[0], lines[0].cells.get(settings.controlMeasure  + '#I')); 
      }




      //##### prise des valeurs existantes sur la mesure LIST
      lines?.forEach(line => {
        if(!line.cells?.get(fkd.fullCode)) {
          if(!line.cells) {
            line.cells = new Map();
          }
          line.cells.set(fkd.fullCode, { k: line.key, m: fkd.fullCode, l: line.level, sc: line.bl.scen });
        }
        // objet Json sur la line
        let fullResult: string = BaselineEngine.getValue(fei, line, line.cells.get(fkd.fullCode)); 
        if(!!fullResult) {
          let jsonRes: any = JSON.parse(fullResult);
          Object.keys(jsonRes).forEach(s => {
            if(valueMap.has(s)) { // gestion des aggrégats... TODO TODO booléens ?
              let myVal: number = Number(jsonRes[s]);
              if(!!myVal) {
                let currentVal: number = Number(valueMap.get(s)) || 0;
                currentVal += myVal;
                // console.log('key',s, jsonRes.s, currentVal);
                valueMap.set(s, currentVal.toString());
              }
            } else {
              valueMap.set(s, jsonRes[s]);
            }
          })
        }
      });

      // prise de restriction si le FD possède une valeur sur la ligne
      let incexKeys: IncExcKeyVO[] = [];

      // création du faux step en level 1
      step = new VisionStep();
      step.domain = fakeVis.domain;
      step.businessUnitCode = fakeVis.businessUnitCode;
      step.visionCode = fakeVis.code;
      step.chronoCode = fakeVis.chronoCode;
      step.code = 'STEP1';
      step.level = 1;
      steps[step.level]= step;

      let order: number = 1;
      settings.keys?.sort((a, b) => a.order - b.order).forEach((key: LFKey) => {
        const parts: string[] = key.FD.split(WizConstants.CONTEXT_SEPARATOR);
        freedata = this.model.freedataEngine.getFreeData(parts[0]);
        if(parts.length === 1) { // il faut un niveau, si on met juste le FD
          parts[1] = "1";
        }
        
        // fausse visionKey
        visionKey = new VisionKey();
        visionKey.domain = fakeVis.domain;
        visionKey.businessUnitCode = fakeVis.businessUnitCode;
        visionKey.code = parts.join(WizConstants.CONTEXT_SEPARATOR);
        visionKey.visionCode = fakeVis.code;
        visionKey.chronoCode = fakeVis.chronoCode;
        visionKey.level = 1;
        visionKey.levelCode = null;
        visionKey.stepLevel = 1;
        visionKey.order = order;
        vkeys[visionKey.order]= visionKey;
        order++;

        // on pose éventuellement un filtre (si une ligne)
        let rawVal: string = null;
        if(lines?.length > 0) {
          rawVal = BaselineEngine.getLabel(fei, lines[0], key.FD, WizConstants.FIELD_CODE); // va retourne le libellé lié à la propriété fdvfield
        }
        if(!!rawVal) {
          let incex: IncExcKey = new IncExcKey();
          incex.inc = true;
          incex.key = [key.FD, rawVal].join(WizConstants.CONTEXT_SEPARATOR);
          incexKeys.push(new IncExcKeyVO(this.model, incex));
        }

        // et création des colonnes
        key.fields.forEach((field: LFField) => {

          let confItem: WidgetConfigurationItemObj = this.model.widgetEngine.newWcio(visionKey.code, colIdx);
          confItem.fdvf = field.code;
          if(field.label) { // surcharge du label
            confItem.lbl = field.label; // fkd.label;
          } else { // sinon label du FD
            confItem.lbl = freedata.trsLabel;
          }
          
          confItem.pin = key.order === 1 ? 'left' : null;
    
          this.model.widgetEngine.wcioColParams(confItem, true, 20, field.width || 100); // taille de col
          if(key.order === 1) { // if(colIdx === 1) { // c'est le premier champ, on le met en ligne        
            context.r.push(confItem);
          } else {
            if(context.pmd) { // en mode consolidation de ligne, on passe le reste en data
              confItem.totAgg = AggregateEngine.AGG_VOID; // on neutralise l'aggrégat
              context.d.push(confItem);
            } else {
              context.r.push(confItem);
            }
            
          }   
          colIdx++;
        });
      })

      // alimentation de la fausse vision
      fakeVis.visionSteps = steps; // faux step
      fakeVis.visionKeys = vkeys; // fausses clés de vision
      fakeVis.reset();

      

      // construction des fausses baselines
      const baselines: Map<string, Baseline> = this.model.incExcSampleEngine.buildFakeBaselines(fakeVis, incexKeys).get(1);
      [...baselines.values() || []].forEach(bl => {
        // prise du bon scenar
        bl.scen = WizConstants.SCEN_FAKE_KEY_CODE;

        if(valueMap.has(bl.key)) {
          BaselineEngine.baselineSetValue(bl, targetMeasure.shortCode + '#I', valueMap.get(bl.key));
          fei.model.cellMatrixEngine.addCell(bl.level, bl.key, bl.scen, targetMeasure.shortCode + '#I', valueMap.get(bl.key));
        } else { // on supprime les éventuels fake existants
          fei.model.cellMatrixEngine.deleteRow(bl.level, bl.key, bl.scen);
        }
      });

      // création du FEI fake
      //Array.from(baselines.keys()).forEach((level: number) => { // en fait, un seul level
      const fakeFEI: FormulaEngineInstance = new FormulaEngineInstance();
      fakeFEI.baselines = {};
      fakeFEI.vision = fakeVis.source;
      fakeFEI.visionStep = BaselineEngine.getVisionStepByLevel(fakeVis, 1);
  
      // on crée une entrée en Fake (scenar @@FAKE)
      renderFEI = new FeiVO(fakeFEI, this._model, fakeVis, fei.visionStep, WizConstants.SCEN_FAKE_KEY_CODE);
      renderFEI.model = this.model;
      this.model.feiEngine.buildToolFEIKeys(renderFEI, FeiEngine.TOOL_MODE_LIST, [...baselines.values()]);

      // ajout de la mesure principale
      if(targetMeasure) { // la mesure cible pour la saisie     
        let size: number = 100;
        if(targetMeasure.datakind === FIELD_KIND.BOOLEAN) {
          size = 70;
        } else if(targetMeasure.datakind === FIELD_KIND.DATE) {
          size = 120;
        }

        // faux FKD qui portera la mesure
        renderFakeFKD = BaselineEngine.buildFeiKeyDef(fei, KEY_KIND.MEASURE, targetMeasure.source, WizConstants.MEASURE_MODE_INTERACT)
        .setParams(true, true, null)
        .setSize(size,size);
        renderFakeFKD.listField = true; // on marque la mesure comme un type liste
        renderFEI.keys.push(renderFakeFKD);

        // et ajout de la colonne
        let confItem: WidgetConfigurationItemObj = this.model.widgetEngine.newWcio(targetMeasure.shortCode + '#I', colIdx);
        confItem.lbl = targetMeasure.trsLabel;
        confItem.totAgg = targetMeasure.defaultAgg || null;
        context.d.push(confItem);
        colIdx++;
      }

      // ajout des mesures de contrôle si existantes. Uniquement au fakeFEI, pas besoin des colonnes
      if(controlMeasureSource) { // on a bien une mesure de contrôle
        controlMeasure = cloneDeep(controlMeasureSource.source);
        // controlMeasure.valueControl = targetMeasure.shortCode + '#I';
        controlMeasure.valueControl = settings.controlKind || '=';
        controlMeasure.valueControl += controlMeasureValue;
        renderFakeFKD = BaselineEngine.buildFeiKeyDef(fei, KEY_KIND.MEASURE, controlMeasure, WizConstants.MEASURE_MODE_INTERACT);
        renderFEI.keys.push(renderFakeFKD);
      }



      // création de la map de clé sur le fakeFEI
      renderFEI.keyMap = new Map(renderFEI.keys.map(key => [key.fullCode, key]));

    } catch(e: any) {
      console.log('buildGridParameters err: ', e);
      return null;
    }

    let filter: string = settings.filter || null;

    let result: ListFieldGridDatas = {fei, 
              measure: targetMeasure?.source, 
              valueMap, 
              renderFEI, 
              context, 
              filter,
              subTotalControl: controlMeasure,
              // grandTotalControl: grandTotalControlMeasure,
            };
    return result;
  }

  /**
   * construction des paramètres du GRID pour un FDV
   * @param fei
   * @param fkd 
   * @param lines
   */
  public buildGridParametersForFD(fei: FeiVO, fkd: FeiKeyDef, fdv: FreedataValueVO, arg: FreedataArgsVO): ListFieldGridDatas {
    
    // la vraie mesure (LIST)
    let sourceMeasure: Measure = fkd.measure; 
    let targetMeasure: MeasureVO;

    // la configuration pour le rendu en mode liste
    let settings: LFSettings = sourceMeasure.settings as LFSettings;

    let valueMap: Map<string, string> = new Map(); // les valeurs par codeFDV
    let freedata: FreeDataVO; // FD porteur
    let renderFEI: FeiVO; // fake FEI pour le rendu
    let renderFakeFKD: FeiKeyDef; // fausse KeyDef
    
    // prise de la mesure de contrôle sur les cumuls
    let controlMeasureSource: MeasureVO;
    let controlMeasureValue: string = null;
    let controlMeasure: Measure;

    //##### génération des fausses Baselines pour la restitution par niveaux
    const vkeys: {[key: number]: VisionKey} = {};
    const steps: {[key: number]: VisionStep} = {};
 
    // création des clés pour la fausse vision
    let fakeVis: VisionVO = cloneDeep(this.model.currentVision);
    let visionKey: VisionKey;
    let step: VisionStep;

    // et au passage, création du contexte pour les colonnes de rendu
    let context: WidgetConfigurationObj = getDefaultWidgetConfigurationObj();
    context.r = [];
    context.d = [];
    let colIdx: number = 1; // indice de la colonne

    if(settings.keys.length > 1) {
      context.pmd = true; // mode consolidation pas si un seul FD
    } else {
      context.pmd = false; 
    }

    try {

      // prise de la mesure cible
      targetMeasure = this.model.measureEngine.getMeasure(fei.visionCode, fei.visionStepLevel, settings.measure);

      // et du contrôle sur total
      // TODO TODO 
      // if(settings.controlMeasure && lines?.length > 0) {
      //   controlMeasureSource = this.model.measureEngine.getMeasure(fei.visionCode, fei.visionStepLevel, settings.controlMeasure);
      //   controlMeasureValue = BaselineEngine.getValue(fei, lines[0], lines[0].cells.get(settings.controlMeasure  + '#I')); 
      // }

      let fullResult: string = this.model.freedataEngine.getFDValue(fdv, arg.code);
      if(fullResult) {
        // console.log('result list', fullResult, fdv, arg);
        let jsonRes: any = JSON.parse(fullResult);
        Object.keys(jsonRes).forEach(s => {
          if(valueMap.has(s)) { // gestion des aggrégats... TODO TODO booléens ?
            let myVal: number = Number(jsonRes[s]);
            if(!!myVal) {
              let currentVal: number = Number(valueMap.get(s)) || 0;
              currentVal += myVal;
              // console.log('key',s, jsonRes.s, currentVal);
              valueMap.set(s, currentVal.toString());
            }
          } else {
            valueMap.set(s, jsonRes[s]);
          }
        });
      }

    
      // prise de restriction si le FD possède une valeur sur la ligne
      let incexKeys: IncExcKeyVO[] = [];

      // création du faux step en level 1
      step = new VisionStep();
      step.domain = fakeVis.domain;
      step.businessUnitCode = fakeVis.businessUnitCode;
      step.visionCode = fakeVis.code;
      step.chronoCode = fakeVis.chronoCode;
      step.code = 'STEP1';
      step.level = 1;
      steps[step.level]= step;

      let order: number = 1;
      settings.keys?.sort((a, b) => a.order - b.order).forEach((key: LFKey) => {
        const parts: string[] = key.FD.split(WizConstants.CONTEXT_SEPARATOR);
        freedata = this.model.freedataEngine.getFreeData(parts[0]);
        if(parts.length === 1) { // il faut un niveau, si on met juste le FD
          parts[1] = "1";
        }
        
        // fausse visionKey
        visionKey = new VisionKey();
        visionKey.domain = fakeVis.domain;
        visionKey.businessUnitCode = fakeVis.businessUnitCode;
        visionKey.code = parts.join(WizConstants.CONTEXT_SEPARATOR);
        visionKey.visionCode = fakeVis.code;
        visionKey.chronoCode = fakeVis.chronoCode;
        visionKey.level = 1;
        visionKey.levelCode = null;
        visionKey.stepLevel = 1;
        visionKey.order = order;
        vkeys[visionKey.order]= visionKey;
        order++;

        // on pose éventuellement un filtre (si une ligne)
        // let rawVal: string = "1";
        // // if(lines?.length > 0) {
        // //   rawVal = BaselineEngine.getLabel(fei, lines[0], key.FD, WizConstants.FIELD_CODE); // va retourne le libellé lié à la propriété fdvfield
        // // }
        // if(!!rawVal) {
        //   let incex: IncExcKey = new IncExcKey();
        //   incex.inc = true;
        //   incex.key = [key.FD, rawVal].join(WizConstants.CONTEXT_SEPARATOR);
        //   incexKeys.push(new IncExcKeyVO(this.model, incex));
        // }

        // et création des colonnes
        key.fields.forEach((field: LFField) => {

          let confItem: WidgetConfigurationItemObj = this.model.widgetEngine.newWcio(visionKey.code, colIdx);
          confItem.fdvf = field.code;
          if(field.label) { // surcharge du label
            confItem.lbl = field.label; // fkd.label;
          } else { // sinon label du FD
            confItem.lbl = freedata.trsLabel;
          }
          
          confItem.pin = key.order === 1 ? 'left' : null;
    
          this.model.widgetEngine.wcioColParams(confItem, true, 20, field.width || 100); // taille de col
          if(key.order === 1) { // if(colIdx === 1) { // c'est le premier champ, on le met en ligne        
            context.r.push(confItem);
          } else {
            if(context.pmd) { // en mode consolidation de ligne, on passe le reste en data
              confItem.totAgg = AggregateEngine.AGG_VOID; // on neutralise l'aggrégat
              context.d.push(confItem);
            } else {
              context.r.push(confItem);
            }
            
          }   
          colIdx++;
        });
      })

      // alimentation de la fausse vision
      fakeVis.visionSteps = steps; // faux step
      fakeVis.visionKeys = vkeys; // fausses clés de vision
      fakeVis.reset();

      

      // construction des fausses baselines
      const baselines: Map<string, Baseline> = this.model.incExcSampleEngine.buildFakeBaselines(fakeVis, incexKeys).get(1);
      [...baselines.values() || []].forEach(bl => {
        // prise du bon scenar
        bl.scen = WizConstants.SCEN_FAKE_KEY_CODE;

        if(valueMap.has(bl.key)) {
          BaselineEngine.baselineSetValue(bl, targetMeasure.shortCode + '#I', valueMap.get(bl.key));
          fei.model.cellMatrixEngine.addCell(bl.level, bl.key, bl.scen, targetMeasure.shortCode + '#I', valueMap.get(bl.key));
        } else { // on supprime les éventuels fake existants
          fei.model.cellMatrixEngine.deleteRow(bl.level, bl.key, bl.scen);
        }
      });

      // création du FEI fake
      //Array.from(baselines.keys()).forEach((level: number) => { // en fait, un seul level
      const fakeFEI: FormulaEngineInstance = new FormulaEngineInstance();
      fakeFEI.baselines = {};
      fakeFEI.vision = fakeVis.source;
      fakeFEI.visionStep = BaselineEngine.getVisionStepByLevel(fakeVis, 1);
  
      // on crée une entrée en Fake (scenar @@FAKE)
      renderFEI = new FeiVO(fakeFEI, this._model, fakeVis, fei.visionStep, WizConstants.SCEN_FAKE_KEY_CODE);
      renderFEI.model = this.model;
      this.model.feiEngine.buildToolFEIKeys(renderFEI, FeiEngine.TOOL_MODE_LIST, [...baselines.values()]);

      // ajout de la mesure principale
      if(targetMeasure) { // la mesure cible pour la saisie     
        let size: number = 100;
        if(targetMeasure.datakind === FIELD_KIND.BOOLEAN) {
          size = 50;
        } else if(targetMeasure.datakind === FIELD_KIND.DATE) {
          size = 120;
        }

        // faux FKD qui portera la mesure
        renderFakeFKD = BaselineEngine.buildFeiKeyDef(fei, KEY_KIND.MEASURE, targetMeasure.source, WizConstants.MEASURE_MODE_INTERACT)
        .setParams(true, true, null)
        .setSize(size,size);
        renderFakeFKD.listField = true; // on marque la mesure comme un type liste
        renderFEI.keys.push(renderFakeFKD);

        // et ajout de la colonne
        let confItem: WidgetConfigurationItemObj = this.model.widgetEngine.newWcio(targetMeasure.shortCode + '#I', colIdx);
        confItem.lbl = targetMeasure.trsLabel;
        confItem.totAgg = targetMeasure.defaultAgg || null;
        context.d.push(confItem);
        colIdx++;
      }

      // ajout des mesures de contrôle si existantes. Uniquement au fakeFEI, pas besoin des colonnes
      if(controlMeasureSource) { // on a bien une mesure de contrôle
        controlMeasure = cloneDeep(controlMeasureSource.source);
        // controlMeasure.valueControl = targetMeasure.shortCode + '#I';
        controlMeasure.valueControl = settings.controlKind || '=';
        controlMeasure.valueControl += controlMeasureValue;
        renderFakeFKD = BaselineEngine.buildFeiKeyDef(fei, KEY_KIND.MEASURE, controlMeasure, WizConstants.MEASURE_MODE_INTERACT);
        renderFEI.keys.push(renderFakeFKD);
      }



      // création de la map de clé sur le fakeFEI
      renderFEI.keyMap = new Map(renderFEI.keys.map(key => [key.fullCode, key]));

    } catch(e: any) {
      console.log('buildGridParameters err: ', e);
      return null;
    }

    let filter: string = settings.filter || null;

    let result: ListFieldGridDatas = {fei, 
              measure: targetMeasure?.source, 
              valueMap, 
              renderFEI, 
              context, 
              filter,
              subTotalControl: controlMeasure,
              // grandTotalControl: grandTotalControlMeasure,
            };
    return result;
  }


  /**
   * construction de la collection de clés pour une mesure de type liste
   * @param fei 
   * @param fkd 
   * @returns la collection bien propre
   */
  private generateListValues(fei: FeiVO, fkd: FeiKeyDef): Map<string, IWidgetFilterItem> {
    let result: Map<string, IWidgetFilterItem> = new Map();
    let entry: IWidgetFilterItem; // l'item à créer
    let rawVal: string; // la valeur brute sur la Widgetline, qui sert de clé

    // on recycle la construction des parameters. ça générer correctement des fausses baselines
    let fullPack: ListFieldGridDatas = this.model.listFieldEngine.buildGridParameters(fei, fkd, []);
    
    // prendre les rendus dans l'ordre du settings
    let freedata: FreeDataVO; // FD porteur
    let settings: LFSettings = fkd.measure.settings as LFSettings;
    let renderers: LFStructuralRender[] = []; // les champs à afficher par NIVEAU (de la clé des éléments de mesure)
    let renderer: LFStructuralRender;
    let sub: LFStructuralSubRender;
    
    let order: number = 0;
    // let nextLevel: boolean = false;
    // construction de la map de rendus
    settings?.keys?.sort((a, b) => a.order - b.order).forEach((key: LFKey) => {
      // suppression éventuelle du niveau
      const parts: string[] = key.FD.split(WizConstants.CONTEXT_SEPARATOR);
      // prise du freedata
      freedata = this.model.freedataEngine.getFreeData(parts[0]);
      let idx: number = 1;
      // let len = key.fields.length;
      
      renderer = {order, freedata, field: WizConstants.FIELD_PARENT_CODE, subs: []}; 
      renderers.push(renderer);

      key.fields.forEach((field: LFField) => { // override éventuel du rendu
        if(field.render) {
          sub = {field: field.code};// , parenthesis: idx < len, nextLevel: nextLevel}; 
          renderer.subs.push(sub);
        }
        idx++;
        // nextLevel = false;
      });
      order++;
      // nextLevel = renderers.length > 0 ? true : false; // seulement si on a pris quelque chose
    });

    // on construit le label pour chaque ligne
    let rawVals: string[];
    let rawKey: string;
    let labels: string[];
    let fdv: FreedataValueVO;
    let subLabels: string[];

    fullPack?.renderFEI?.getAllLines()?.forEach(wl => {
      
      rawVal = wl.key; // la clé complète pour cette ligne
      rawVals = rawVal.split(WizConstants.KEY_SEPARATOR);
      labels = []; // préparation des labels par niveaux

      entry = {rawVal, sortKey: rawVal, label: rawVal, rawVals, labels}; // on crée l'entrée brute, tout rawVal

      for(let i = 0; i < renderers.length; i++) { // pour chaque niveau de rendu
        
        renderer = renderers[i];
        rawKey = rawVals[renderer.order]; // prise de la valeur de FDV sur la clé pour ce niveau
        fdv = renderer.freedata.getFreeDataValue(rawKey); 

        if(renderer.subs?.length > 0) {
          subLabels = [];
          for(let j = 0; j < renderer.subs.length; j++) { // pour chaque champ désiré
            sub = renderer.subs[j];
            let parenthesis: boolean = j < renderer.subs.length - 1;
            let baseLbl: string = this.model.freedataEngine.getFDValue(fdv, sub.field);
            if(parenthesis) { // suis pas le dernier, ajout des parenthèses
              subLabels.push('(' + baseLbl + ')');
            } else {
              subLabels.push(baseLbl);
            }
          };
          labels.push(subLabels.join(' '));
        } else { // pas de demande de rendu pour ce niveau
          labels.push('');
        }
      }
      
      let toKeep: string[] = [];
      labels.forEach(l => {
        if(l) {
          toKeep.push(l);
        }
      })
      entry.label = toKeep.join(" - ");
      result.set(rawVal, entry);
    });

    return result;
  }

  /**
   * construit les valeurs possibles pour un filtre sur une mesure de type liste
   */
  public generateFilterCollection(filter: IWidgetFilter): void {
    if(!filter.widget.fei.listMeasureRenderMap?.has(filter.fkd.fullCode)) {
      let coll: Map<string, IWidgetFilterItem> = this.generateListValues(filter.widget.fei, filter.fkd);
      if(!filter.widget.fei.listMeasureRenderMap) {
        filter.widget.fei.listMeasureRenderMap = new Map();
      }
      filter.widget.fei.listMeasureRenderMap.set(filter.fkd.fullCode, coll);
    }
    filter.listMeasureCollection = filter.widget.fei.listMeasureRenderMap.get(filter.fkd.fullCode);
  }


/**
 * génération des rendus de types listes pour les FDVs
 * @param fei 
 * @param fdv 
 * @param arg
 * @param values existantes
 * @param itemSeparator 
 * @returns 
 */
public generateRendererForFDV(fei: FeiVO, fdv: FreedataValueVO, arg: FreedataArgsVO, values: string, itemSeparator: string = ', '): string {
  //#### construction de la map de rendus
  if(!fei) { // accès depuis la vue Freedata
    return '';
  }

  const fakeMeas: MeasureVO = this.model.measureEngine.buildFake();
  fakeMeas.code = arg.code;
      fakeMeas.shortCode = arg.code;
      fakeMeas.mode = WizConstants.MEASURE_MODE_INTERACT; // interact, donc présentée par défaut
      fakeMeas.datakind = FIELD_KIND.LIST; // on veut une liste
      fakeMeas.settings = arg.settings;
    let fakeFkd: FeiKeyDef = BaselineEngine.buildFeiKeyDef(fei, KEY_KIND.MEASURE, fakeMeas.source, WizConstants.MEASURE_MODE_INTERACT);

  let keys: Map<string, IWidgetFilterItem> = this.generateListValues(fei, fakeFkd);
  
  
  let listKeys: Map<IWidgetFilterItem, any> = new Map(); // gestion des cumuls en mode pivot
  let listKey: IWidgetFilterItem;

  let measure: MeasureVO = this.model.measureEngine.getMeasure(fei.visionCode, fei.visionStepLevel, (arg.settings as LFSettings).measure);
  let mesureIsBool: boolean = measure?.datakind === FIELD_KIND.BOOLEAN;


  // objet Json sur la ligne (la valeur de la mesure liste)
  let jsonResult: string = this.model.freedataEngine.getFDValue(fdv, arg.code);

  // le résultat
  let sets: any = arg.settings;
  let resultStr: string = null;

  if(!!jsonResult) {
    // suppression des ""
    let cocot: RegExp = /\\\"\\\"/;
    let jsonRes: any = "";
    try {
      jsonRes = jsonResult.replace(cocot, ""); // blindage DCMJ contexture... 
      jsonRes = JSON.parse(jsonRes); // c'est une liste de clé:value
      // la clé peut être composite en cas d'utilisation de 2 niveaux

      Object.keys(jsonRes).forEach(jsonKey => {
        let currentVal: number = 0;
        listKey = keys.get(jsonKey);
        if(listKeys.has(listKey)) { // existe déjà, aggrégat
          // TODO gestion des aggrégats...
          currentVal = Number(listKeys.get(listKey)) || 0;
        } 
        currentVal += Number(jsonRes[jsonKey]) || 0;
        listKeys.set(listKey, currentVal.toString());
      });

    resultStr = this.constructRender(keys, listKeys, sets, measure, mesureIsBool, itemSeparator);

    } catch(e: any) {

    }
  }
    return resultStr;
}

  /**
   * construction des chaines de texte
   * @param keys 
   * @param listKeys 
   * @param sets 
   * @param measure
   * @param mesureIsBool 
   * @param itemSeparator 
   * @returns 
   */
  private constructRender(
    keys: Map<string, IWidgetFilterItem>,
    listKeys: Map<IWidgetFilterItem, any>, 
    sets: any,
    measure: MeasureVO,
    mesureIsBool: boolean,
    itemSeparator: string = ', '
  ) : string {
      let result: string[] = [];
      let resultStr: string = null;

      try {
        let witness: IWidgetFilterItem = [...keys.values()][0];
        let nbField: number = witness.rawVals.length;
        
        let previousMaster: string = null;
        let currentMaster: string;

        // map code parent, enfants[]
        // let labelMap: Map<string, Set<string>> = new Map();
        let labelMap: Map<string, {label?: string, value?: string, sort?: any}[]> = new Map();
        let baseItem: {label?: string, value?: string, sort?: any} = null;
        
        // let sort: any = (fkd.measure.settings as any).sort;
        let sorField: string = sets.sort?.field || 'label';
        let sortWay: string = sets.sort?.way || 'ASC';
        //console.log("sort", sorField, sortWay);

        [...listKeys.keys()].sort((a, b) => a.sortKey.localeCompare(b.sortKey)).forEach(iwf => {     
          if(nbField > 1 ) { // gigogne, on regroupe par levels < au dernier
            currentMaster = iwf.rawVals[0];
            if(currentMaster != previousMaster) { // premier ou différent
              // labelMap.set(iwf.labels[0], new Set());
              labelMap.set(iwf.labels[0], []);
            }

            // baseLbl = iwf.labels[1];
            baseItem = {};
            baseItem.label = iwf.labels[1];
            if(!mesureIsBool) {
              
              // baseLbl += ': ' + this.model.formatEngine.applyFormat(listKeys.get(iwf), measure.datakind, measure.formatCode, AggregateEngine.AGG_SUM) ; // TODO Format 
              baseItem.value =  this.model.formatEngine.applyFormat(listKeys.get(iwf), measure.datakind, measure.formatCode, AggregateEngine.AGG_SUM) ; // TODO Format 
              baseItem.sort = listKeys.get(iwf);
            }
            // labelMap.get(iwf.labels[0]).add(baseLbl);
            labelMap.get(iwf.labels[0]).push(baseItem);

          } else {
            currentMaster = "";
            if(!labelMap.has(currentMaster)) {
              // labelMap.set(currentMaster, new Set());
              labelMap.set(currentMaster, []);
            }
            baseItem = {};
            baseItem.label = iwf.label;
            // baseLbl = iwf.label;
            if(!mesureIsBool) {
              // baseLbl += ': ' + this.model.formatEngine.applyFormat(listKeys.get(iwf), measure.datakind, measure.formatCode, AggregateEngine.AGG_SUM) ; // TODO Format 
              baseItem.value =  this.model.formatEngine.applyFormat(listKeys.get(iwf), measure.datakind, measure.formatCode, AggregateEngine.AGG_SUM) ; // TODO Format 
              baseItem.sort = listKeys.get(iwf);
            }
            // labelMap.get(currentMaster).add(baseLbl);
            labelMap.get(currentMaster).push(baseItem);
          }

          previousMaster = currentMaster;
        });

        // gestion du tri
        [...labelMap.keys()].forEach(k => {
          let values:  {label?: string, value?: string, sort?: any}[] = labelMap.get(k);
          if(sorField === 'value') {
            values.sort((a, b) => (a.sort - b.sort));
          } else {
            values.sort((a, b) => a.label.localeCompare(b.label));
          }
          if(sortWay === 'DESC') {
            values.reverse();
          }
        });



        // if(labelMap.size > 1) { // plusieurs parent
          [...labelMap.keys()].forEach(p => {
            // console.log('labelMap+1', labelMap.get(p));
            let vals: string[] = [];
            [...labelMap.get(p)].forEach(obj => {
              if(mesureIsBool) {
                vals.push(obj.label);
              } else {
                vals.push([obj.label, obj.value].join(': '));
              }
            });
            if(labelMap.size > 1) {
              result.push([p, ':[', vals.join(itemSeparator), ']'].join(''));
            } else {
              result.push(vals.join(itemSeparator));
            }
          });

        // } else {
        //   console.log('labelMap', [...labelMap.values()][0]);
        //   result.push([...[...labelMap.values()][0]].join(', '));
        // }

        resultStr = result.join(' - ');
        
        return resultStr;
        
      } catch(e: any) {

      }

      return resultStr;
    }

  /**
   * construction du rendu sous forme de liste pour la mesure
   * @param fei cible
   * @param fkd 
   * @param lines pool de lignes de la cellule (possible aggrégat)
   * @param itemSeparator pour forcer le retour à la ligne en mode Form
   * @returns le rendu formaté.
   */
  public generateRenderer(fei: FeiVO, fkd: FeiKeyDef, lines: WidgetLine[], itemSeparator: string = ', '): string {
    
    if(!fei) { // mode création dans form
      return '';
    }

    //#### construction de la map de rendu si nécessaire
    let keys: Map<string, IWidgetFilterItem>;

    if(!fei.listMeasureRenderMap?.has(fkd.fullCode)) {
      keys = this.generateListValues(fei, fkd);
      if(!fei.listMeasureRenderMap) {
            fei.listMeasureRenderMap = new Map();
      }
      fei.listMeasureRenderMap.set(fkd.fullCode, keys);
    } 
    keys = fei.listMeasureRenderMap.get(fkd.fullCode);
    
    
    let listKeys: Map<IWidgetFilterItem, any> = new Map(); // gestion des cumuls en mode pivot
    let listKey: IWidgetFilterItem;
    // TODO prise du type de mesure pour gestion des aggrégats
    let measure: MeasureVO = this.model.measureEngine.getMeasure(fei.visionCode, fei.visionStepLevel, (fkd.measure.settings as LFSettings).measure);
    let mesureIsBool: boolean = measure?.datakind === FIELD_KIND.BOOLEAN;

    
    //##### prise des valeurs
    lines.forEach(line => { // ici, ce sont de vraies baselines  
      if(line.cells?.get(fkd.fullCode)) { // inutile si pas de valeur / pas de cellule
        
        // objet Json sur la ligne (la valeur de la mesure liste)
        let jsonResult: string = BaselineEngine.getValue(fei, line, line.cells.get(fkd.fullCode)); 

        if(!!jsonResult) {
          // suppression des ""
          let cocot: RegExp = /\\\"\\\"/;
          let jsonRes: any = "";
          try {
            jsonRes = jsonResult.replace(cocot, ""); // blindage DCMJ contexture... 
            jsonRes = JSON.parse(jsonRes); // c'est une liste de clé:value
            // la clé peut être composite en cas d'utilisation de 2 niveaux

            Object.keys(jsonRes).forEach(jsonKey => {

              let currentVal: number = 0;
              listKey = keys.get(jsonKey);
              if(listKeys.has(listKey)) { // existe déjà, aggrégat
                // TODO gestion des aggrégats...
                currentVal = Number(listKeys.get(listKey)) || 0;
              } 
              currentVal += Number(jsonRes[jsonKey]) || 0;
              listKeys.set(listKey, currentVal.toString());
            });
          } catch(e: any) {

          }
        }
      }
    });


    //##### on a consolidé, gestion des rendus 
    let sets: any = fkd.measure.settings;
    let result: string[] = [];
    let resultStr: string = null;

    resultStr = this.constructRender(keys, listKeys, sets, measure, mesureIsBool, itemSeparator);

    return resultStr;
  }

  /* ##############################################################################
       gestion des seuils
  ############################################################################## */

/**
 * génération des rendus de types seuils pour les FDVs
 * @param fei 
 * @param fdv 
 * @param arg
 * @param values existantes
 * @param itemSeparator 
 * @returns 
 */
public generateThresholdRendererForFDV(fei: FeiVO, fdv: FreedataValueVO, arg: FreedataArgsVO, values: string, itemSeparator: string = ', '): string {
  
  if(!values) {
    return '';
  }
  
  // console.log('arg', arg, values);

  let settings: any = arg.settings;
  
  // construction des champs 
  let fields: ThresholdField[] = [];
  settings.keys.forEach( k => {

        let f: ThresholdField = {"code": k.code, "kind": k.kind, "sort": k.sort, "label": k.label, order: 0, format:k.format || null};
        fields.push(f);
  });

  let protoArr: any[] = JSON.parse(values);
  // console.log('protoArr', protoArr);
  let lines: ThresholdLine[] = [];
    
  protoArr?.forEach(protoItem => {
    let values: Map<string, any> = new Map();
    let uk: any;
    fields.forEach(tf =>{
      if(protoItem[tf.code] !== undefined) {
        let isnum = this.model.dataEngine.datakindIsNumeric(tf.kind); 
        if(isnum) {
          values.set(tf.code, Number(protoItem[tf.code]));
        } else {
          values.set(tf.code, protoItem[tf.code]);
        }
        if(tf.code === fields[0].code) {
          uk = protoItem[tf.code];
        }
      }
    });
    let line: ThresholdLine = {uk, values};
    lines.push(line);
  });

  let isnum = this.model.dataEngine.datakindIsNumeric(fields[0].kind); 
  if(isnum) {
    lines = lines.sort((a: ThresholdLine, b: ThresholdLine) => {
      if (a.values.get(fields[0].code) == null && b.values.get(fields[0].code) == null) return 0;
      if (a.values.get(fields[0].code) == null) return 1;   // A après B
      if (b.values.get(fields[0].code) == null) return -1;  // B après A
      return (a.values.get(fields[0].code) - b.values.get(fields[0].code));
    });
  } else {
    // console.log('not num');
    // toujours num en fait...
  }
  
  // construction du texte
  let lineStr: string[] = [];
  lines.forEach(tsl => {
    let parts: string[] = [];
    let goodStr: string;
    fields.forEach(tsf => {
      goodStr = Model.getInstance().formatEngine.applyFormat(tsl.values.get(tsf.code)?.toString(), tsf.kind, tsf.format, null);
      parts.push(goodStr);
    });
    lineStr.push(parts.join(': '));
  });

  return lineStr.join(itemSeparator);
}

/**
 * génération des rendus de types seuils
   * @param fei cible
   * @param fkd 
   * @param lines pool de lignes de la cellule (possible aggrégat)
   * @param itemSeparator pour forcer le retour à la ligne en mode Form
   * @returns le rendu formaté.
 */
public generateThresholdRenderer(fei: FeiVO, fkd: FeiKeyDef, wls: WidgetLine[], itemSeparator: string = ', '): string {
  
    if(!fei || wls?.length <= 0) { // mode création dans form
      return '';
    }

  
  // console.log('arg', arg, values);

  let settings: any = fkd.measure?.settings;
  
  // construction des champs 
  let fields: ThresholdField[] = [];
  settings.keys.forEach( k => {

        let f: ThresholdField = {"code": k.code, "kind": k.kind, "sort": k.sort, "label": k.label, order: 0, format:k.format || null};
        fields.push(f);
  });

  // prise de la valeur sur la 1ère ligne
  let theOne: WidgetLine = wls[0];
  // objet Json sur la ligne (la valeur de la mesure liste)
  let jsonResult: string;

  if(theOne?.cells?.get(fkd.fullCode)) { // inutile si pas de valeur / pas de cellule
   jsonResult = BaselineEngine.getValue(fei, theOne, theOne.cells.get(fkd.fullCode)); 
  } else {
    return '';
  }
        

  let protoArr: any[] = JSON.parse(jsonResult);
  // console.log('protoArr', protoArr);
  let lines: ThresholdLine[] = [];
    
  protoArr?.forEach(protoItem => {
    let values: Map<string, any> = new Map();
    let uk: any;
    fields.forEach(tf =>{
      if(protoItem[tf.code] !== undefined) {
        let isnum = this.model.dataEngine.datakindIsNumeric(tf.kind); 
        if(isnum) {
          values.set(tf.code, Number(protoItem[tf.code]));
        } else {
          values.set(tf.code, protoItem[tf.code]);
        }
        if(tf.code === fields[0].code) {
          uk = protoItem[tf.code];
        }
      }
    });
    let line: ThresholdLine = {uk, values};
    lines.push(line);
  });

  let isnum = this.model.dataEngine.datakindIsNumeric(fields[0].kind); 
  if(isnum) {
    lines = lines.sort((a: ThresholdLine, b: ThresholdLine) => {
      if (a.values.get(fields[0].code) == null && b.values.get(fields[0].code) == null) return 0;
      if (a.values.get(fields[0].code) == null) return 1;   // A après B
      if (b.values.get(fields[0].code) == null) return -1;  // B après A
      return (a.values.get(fields[0].code) - b.values.get(fields[0].code));
    });
  } else {
    // console.log('not num');
    // toujours num en fait...
  }
  
  // construction du texte
  let lineStr: string[] = [];
  lines.forEach(tsl => {
    let parts: string[] = [];
    let goodStr: string;
    fields.forEach(tsf => {
      goodStr = Model.getInstance().formatEngine.applyFormat(tsl.values.get(tsf.code)?.toString(), tsf.kind, tsf.format, null);
      parts.push(goodStr);
    });
    lineStr.push(parts.join(': '));
  });

  return lineStr.join(itemSeparator);
}

}
